import { library } from '@fortawesome/fontawesome-svg-core';
import { faEdit, faEllipsisV, faUnlink } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslator } from '@Localization';
import { Dropdown, IconButton, Menu, MenuButton, MenuItem } from '@mui/joy';
import { observer } from 'mobx-react-lite';
import { MouseEvent, ReactElement, useContext } from 'react';
import { LazyLoadComponent } from 'react-lazy-load-image-component';
import { S_Checkbox } from '../../../../design/inputs/checkbox/S_Checkbox';
import { S_Link2 } from '../../../../design/links/S_Link2';
import { S_ClampLines } from '../../../../design/misc/S_ClampLines';
import { EditIcon } from '../../../../design/static/icons/EditIcon';
import { TrashIcon } from '../../../../design/static/icons/TrashIcon';
import { LazyImage } from '../../../components/LazyImage';
import { EnableFor } from '../../../containers/EnableForRole';
import { Home } from '../../../routes/config/Home';
import { DatasetDetails } from '../DatasetDetails.context';
import { useDatasetPermissions } from '../datasets.permissions';
import { IDatasetDetailsImage } from '../datasetsDetails.store';

library.add(faUnlink, faEdit);

interface IDatasetThumbnailProps {
  image: IDatasetDetailsImage;
  workspaceId: string;
  disabled: boolean;
  datasetId: string;
  authToken: string;
  isChecked: boolean;
  onCheck(id: string, isBatch: boolean): void;
  onDelete(id: string): void;
  onRename(id: string): void;
}

export const DatasetThumbnail = observer((props: IDatasetThumbnailProps): ReactElement => {
  const { t } = useTranslator();
  const permissions = useDatasetPermissions();
  const context = useContext(DatasetDetails);
  const canDeleteUnmarkedImages = permissions.canDeleteUnmarkedImages(context.store.details.status, context.store.details.createdById);
  const canDeleteMarkedImages = permissions.canDeleteMarkedImages();
  const canRenameImages = permissions.canRenameImages(context.store.details.status, context.store.details.createdById);
  const canDownloadImages = permissions.canDownloadImages(context.store.details.status, context.store.details.createdById);

  const getNavigationUrl = (imageId: string): string => {
    const { datasetId, workspaceId } = props;
    return Home.Datasets.Details.Preview.withParams({ datasetId, imageId, workspaceId });
  };

  const renderImage = (): ReactElement => {
    return (
      <LazyLoadComponent threshold={300}>
        <LazyImage
          source={props.image.url}
          headers={new Headers({ Authorization: `Bearer ${props.authToken}` })}
        />
      </LazyLoadComponent>
    );
  };

  const renderName = (): ReactElement => (
    <S_ClampLines
      lines={2}
      text={props.image.name}
    />
  );

  const handleOnClick = (e: MouseEvent, id: string): void => {
    if (canDeleteMarkedImages.allowed || (!props.image.hasAnnotations && !props.image.hasLocks) || canDownloadImages) {
      props.onCheck(id, e.nativeEvent.shiftKey);
    }
  };

  const disabled = !canDeleteMarkedImages.allowed && !canDownloadImages && (props.image.hasAnnotations || props.image.hasLocks);
  const canDeleteImage = canDeleteMarkedImages || (canDeleteUnmarkedImages && !props.image.hasLocks && !props.image.hasAnnotations);
  let title = '';
  if (!canDeleteMarkedImages.allowed && !canDownloadImages) {
    if (props.image.hasLocks) {
      title = 'image_is_already_locked';
    } else if (props.image.hasAnnotations) {
      title = 'image_is_already_marked';
    }
  }

  return (
    <div className='thumb'>
      <div className='image-container'>
        {props.disabled && renderImage()}
        {!props.disabled && (
          <div className='image-wrapper'>
            <S_Link2
              color='text'
              to={getNavigationUrl(props.image.id)}
            >
              {renderImage()}
            </S_Link2>
            {canDeleteUnmarkedImages && (
              <div
                className='thumb-checkbox'
                title={title}
              >
                <S_Checkbox
                  checked={props.isChecked}
                  onClick={(e) => {
                    handleOnClick(e, props.image.id);
                  }}
                  value={props.image.id}
                  id={props.image.id}
                  disabled={disabled}
                  className={`${props.isChecked ? 'visible' : ''} ${disabled ? 'locked' : ''}`}
                />
              </div>
            )}
            <Dropdown>
              <MenuButton
                className='thumb-dropdown'
                slots={{ root: IconButton }}
                slotProps={{ root: { sx: { '&:hover': { backgroundColor: 'transparent' } } } }}
              >
                <FontAwesomeIcon icon={faEllipsisV} />
              </MenuButton>
              <Menu placement='bottom-start'>
                <EnableFor hasPermission={canRenameImages}>
                  <MenuItem
                    onClick={(): void => {
                      props.onRename(props.image.id);
                    }}
                  >
                    <EditIcon />
                    {t('rename_image')}
                  </MenuItem>
                </EnableFor>
                <EnableFor hasPermission={canDeleteUnmarkedImages}>
                  <MenuItem
                    disabled={!canDeleteImage}
                    onClick={(): void => {
                      props.onDelete(props.image.id);
                    }}
                  >
                    <TrashIcon />
                    {t('delete_image')}
                  </MenuItem>
                </EnableFor>
              </Menu>
            </Dropdown>
          </div>
        )}
      </div>
      <div className='description'>
        <div className='file-name'>
          {props.disabled ? (
            renderName()
          ) : (
            <S_Link2
              color='text'
              title={props.image.name}
              to={getNavigationUrl(props.image.id)}
            >
              {renderName()}
            </S_Link2>
          )}
        </div>
        <div className='attributes'>
          <span className='attribute'>
            {t('image_size')}:<span className={'attribute-value'}> {props.image.size.toFixed(2)}</span>
          </span>
          <span className='attribute'>
            {t('resolution')}:<span className={'attribute-value'}> {`${props.image.width}x${props.image.height}`}</span>
          </span>
        </div>
      </div>
    </div>
  );
});
